import React, { forwardRef, useCallback } from 'react';
import { Tooltip } from '@web-chat-core/ui-kit';
import { ObstacleId } from 'features/MutedSpeechDetection/guardController';
import { ControlButtonMenu, MenuItem } from '../ControlButtonMenu/ControlButtonMenu';
import { ButtonVariant, ShapeVariant, SizeVariant } from './types';
import { ButtonSeparator, MainButton, StyledControlButton } from './view';

interface ButtonProps {
    children?: React.ReactNode;
    variant?: ButtonVariant;
    active?: boolean;
    label?: string;
    disabled?: boolean;
    onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
    IconImage?: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string | undefined }>;
    title?: string;
    menuItems?: MenuItem[];
    onMenuItemClick?: (item: MenuItem) => void;
    withoutMenuShadow?: boolean;
    shape?: ShapeVariant;
    size?: SizeVariant;
    className?: string;
    tabIndex?: number;
    menuId?: ObstacleId;
}

export const ControlButton = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            label,
            disabled,
            onClick,
            IconImage,
            title,
            menuItems,
            onMenuItemClick,
            withoutMenuShadow,
            className,
            active = true,
            variant = 'basic',
            shape = 'oval',
            size = 'medium',
            tabIndex,
            menuId,
        }: ButtonProps,
        forwardedRef,
    ) => {
        const hasMenuItems = !!menuItems && menuItems.length > 0;

        const handleClick = useCallback(
            (e: React.MouseEvent<HTMLButtonElement>) => {
                onClick?.(e);
            },
            [onClick],
        );

        const button = (
            <MainButton
                tabIndex={tabIndex}
                ref={forwardedRef}
                $variant={variant}
                $on={active}
                $hasDropdown={hasMenuItems}
                $shape={shape}
                $size={size}
                disabled={disabled}
                onClick={handleClick}
                className={className}
            >
                {IconImage ? <IconImage /> : null}
                {label ? label : null}
                {children}
            </MainButton>
        );

        const buttonWithTooltip = title ? (
            <Tooltip label={title} whiteSpace="pre-line">
                {button}
            </Tooltip>
        ) : (
            button
        );

        return (
            <StyledControlButton>
                {buttonWithTooltip}
                {hasMenuItems ? (
                    <>
                        <ButtonSeparator $on={active} />
                        <ControlButtonMenu
                            menuId={menuId}
                            menuItems={menuItems}
                            onMenuItemClick={onMenuItemClick}
                            active={active}
                            disabled={disabled}
                            withoutShadow={withoutMenuShadow}
                        />
                    </>
                ) : null}
            </StyledControlButton>
        );
    },
);
